import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Checkbox, FormControlLabel, FormGroup, MenuItem, TextField } from '@mui/material';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import ConfirmationModal from '../../ConfirmationModal';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import { Document } from 'store/types/Meeting';
import { ActivityType, ConfirmationType, ItemType } from 'store/enums/BallotReviewTypes';
import { defaultSnackbarErrorProps } from 'util/layout';
import { useSafeSelectIdValue } from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

type DocumentSectionProps = {
  onResetAll?: () => void;
};

const getDocumentOptions = (documents: Document[]) =>
  documents.map((d) => (
    <MenuItem key={d.id} value={d.id}>
      {d.lineItemNumber ? `Doc #: ${d.number} - Line #: ${d.lineItemNumber}` : `Doc #: ${d.number}`}
    </MenuItem>
  ));

const idLabel = 'document';

const DocumentSection: React.FunctionComponent<DocumentSectionProps> = ({ onResetAll }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity, documents, setCurrentDocument, saveDocument, deleteDocument } = useMeetingStaffContext();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [draftDocumentId, setDraftDocumentId] = useState(() => `${idLabel}-${Date.now()}`);
  const [number, setNumber] = useState('');
  const [title, setTitle] = useState('');
  const [enableLineItem, setEnableLineItem] = useState(false);
  const [lineItemNumber, setLineItemNumber] = useState('');
  const [lineItemTitle, setLineItemTitle] = useState('');

  const documentOptions = useMemo(() => getDocumentOptions(documents), [documents]);

  const safeSelectedDocumentId = useSafeSelectIdValue(selectedDocumentId, documents);

  const currentDocument: Document = useMemo(() => {
    return {
      id: selectedDocumentId || draftDocumentId,
      number,
      title,
      lineItemNumber: enableLineItem ? lineItemNumber : undefined,
      lineItemTitle: enableLineItem ? lineItemTitle : undefined,
    };
  }, [selectedDocumentId, draftDocumentId, number, title, enableLineItem, lineItemNumber, lineItemTitle]);

  const handleDocumentChange = useCallback((event) => {
    const documentId = event.target.value;

    setSelectedDocumentId(documentId);
    if (!documentId) {
      setDraftDocumentId(`${idLabel}-${Date.now()}`);
    }
  }, []);

  const handleSaveDocument = useCallback(() => {
    saveDocument?.().then((saved) => {
      if (saved) {
        enqueueSnackbar(`Document saved`, { variant: 'success' });
        setSelectedDocumentId(saved.id);
        setDraftDocumentId(`${idLabel}-${Date.now()}`);
      }
    });
  }, [enqueueSnackbar, saveDocument]);

  const handleDeleteDocument = useCallback(() => {
    if (!selectedDocumentId) {
      enqueueSnackbar(`No document selected to delete`, { variant: 'warning' });
      return;
    }

    deleteDocument?.(selectedDocumentId);
  }, [deleteDocument, enqueueSnackbar, selectedDocumentId]);

  const handleDisplayDocument = useCallback(() => {
    saveDocument?.().then((saved) => {
      if (!saved) return;

      setSelectedDocumentId(saved.id);
      setDraftDocumentId(`${idLabel}-${Date.now()}`);

      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentDocument: saved,
        currentMotion: undefined,
        currentVotingSession: undefined,
        currentReject: undefined,
        currentTechnicalIssue: undefined,
        currentComment: undefined,
        currentApprovalLevel: undefined,
      })
        .then(() => {
          logActivity?.({
            type: ActivityType.Document,
            document: saved,
          });

          enqueueSnackbar(`Displayed document`, { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(`Failed to display document`, defaultSnackbarErrorProps);
          console.error('Error displaying document:', error);
        });
    });
  }, [enqueueSnackbar, logActivity, meetingId, meetingSession, saveDocument]);

  const handleClearDocument = useCallback(() => {
    setSelectedDocumentId('');
    setDraftDocumentId(`${idLabel}-${Date.now()}`);
    setNumber('');
    setTitle('');
    setEnableLineItem(false);
    setLineItemNumber('');
    setLineItemTitle('');
    onResetAll?.();

    MeetingService.saveMeetingSession({
      ...meetingSession,
      meetingId: meetingId,
      currentDocument: undefined,
      currentMotion: undefined,
      currentVotingSession: undefined,
      currentReject: undefined,
      currentTechnicalIssue: undefined,
      currentComment: undefined,
      currentApprovalLevel: undefined,
      currentIpCheckVoting: undefined,
      currentIpCheckSession: undefined,
      currentTechnologyConflict: undefined,
    })
      .then(() => {
        enqueueSnackbar(`Cleared document`, { variant: 'success' });
      })
      .catch((error: string) => {
        enqueueSnackbar(`Failed to display document`, defaultSnackbarErrorProps);
        console.error('Error displaying document:', error);
      });
  }, [enqueueSnackbar, meetingId, meetingSession, onResetAll]);

  const handleSaveClick = useCallback(() => {
    if (safeSelectedDocumentId) {
      setSaveModalOpen(true);
      return;
    }
    handleSaveDocument();
  }, [handleSaveDocument, safeSelectedDocumentId]);

  const handleSaveModalSubmit = useCallback(() => {
    setSaveModalOpen(false);
    handleSaveDocument();
  }, [handleSaveDocument]);

  const handleDeleteClick = useCallback(() => {
    if (safeSelectedDocumentId) {
      setDeleteModalOpen(true);
      return;
    }
    handleDeleteDocument();
  }, [handleDeleteDocument, safeSelectedDocumentId]);

  const handleDeleteModalSubmit = useCallback(() => {
    setDeleteModalOpen(false);
    handleDeleteDocument();
  }, [handleDeleteDocument]);

  useEffect(() => {
    const document = documents.find((d) => d.id === selectedDocumentId);

    setNumber(document?.number || '');
    setTitle(document?.title || '');
    setEnableLineItem(!!document?.lineItemNumber || !!document?.lineItemTitle);
    setLineItemNumber(document?.lineItemNumber || '');
    setLineItemTitle(document?.lineItemTitle || '');
  }, [documents, selectedDocumentId]);

  useEffect(() => {
    setCurrentDocument?.(currentDocument);
  }, [currentDocument, setCurrentDocument]);

  return (
    <>
      <GridContainer>
        <GridItem md={6}>
          <TextField label={'Load Document'} select value={safeSelectedDocumentId} onChange={handleDocumentChange}>
            <MenuItem value={''}>-- New Document --</MenuItem>
            {documentOptions}
          </TextField>
        </GridItem>
        <GridItem md={6} className={commonStyles.buttonGroup}>
          <Button variant="contained" color="primary" onClick={handleSaveClick}>
            Save
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteClick}>
            Delete
          </Button>
        </GridItem>
        <GridItem md={6}>
          <TextField
            label={'Document Number'}
            value={number}
            required
            onChange={(event) => {
              setNumber(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem md={6}>
          <TextField
            label={'Title'}
            value={title}
            required
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableLineItem}
                  onChange={(event) => {
                    setEnableLineItem(event.target.checked);
                  }}
                />
              }
              label="Enable Line Item"
            />
          </FormGroup>
        </GridItem>
        {enableLineItem && (
          <>
            <GridItem md={6}>
              <TextField
                label={'Line Item Number'}
                value={lineItemNumber}
                onChange={(event) => {
                  setLineItemNumber(event.target.value);
                }}
              ></TextField>
            </GridItem>
            <GridItem md={6}>
              <TextField
                label={'Line Item Title'}
                value={lineItemTitle}
                onChange={(event) => {
                  setLineItemTitle(event.target.value);
                }}
              ></TextField>
            </GridItem>
          </>
        )}
        <GridItem className={commonStyles.buttonGroup}>
          <Button variant="contained" color="secondary" onClick={handleDisplayDocument}>
            Display Document
          </Button>
          <Button variant="contained" color="error" onClick={handleClearDocument}>
            Reset All
          </Button>
        </GridItem>
      </GridContainer>
      <ConfirmationModal
        itemType={ItemType.Document}
        confirmationType={ConfirmationType.Overwrite}
        open={saveModalOpen}
        onSubmit={handleSaveModalSubmit}
        onClose={() => setSaveModalOpen(false)}
      />
      <ConfirmationModal
        itemType={ItemType.Document}
        confirmationType={ConfirmationType.Delete}
        open={deleteModalOpen}
        onSubmit={handleDeleteModalSubmit}
        onClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default DocumentSection;
